import { useCallback } from 'react';
import { useCalibration } from '../../context/calibrationContext';
import { useTestContext } from '../../context/testContext';
import { forthValleyVisualFieldDots } from '../data';

const useDots = () => {
  const { testData } = useTestContext();
  const {
    calibrationData: { screenWidthPx, screenHeightPx, blindSpotVanishingXDistancePx },
  } = useCalibration();

  const isOutOfBoundaries = useCallback(
    (individualDot) => {
      const landoltCDimensions = { x: 150, y: 100 };
      const getVisualFieldBoundaries = (sizeInPx) => {
        const bufferSize = sizeInPx * testData.visualFieldSettings.screenBuffer;
        return sizeInPx - bufferSize * 2;
      };

      const getPixelsForDegree = (degree) => {
        let pxValue = (degree * blindSpotVanishingXDistancePx) / 15;
        if (pxValue < 0) {
          return (pxValue *= -1);
        } else return pxValue;
      };

      if (
        getPixelsForDegree(individualDot['degreeX']) <=
          getVisualFieldBoundaries(screenWidthPx) - landoltCDimensions['x'] / 2 - testData.visualFieldSettings.fixationTargetDimensions.width / 2 &&
        getPixelsForDegree(individualDot['degreeY']) <=
          getVisualFieldBoundaries(screenHeightPx) - landoltCDimensions['y'] / 2 - testData.visualFieldSettings.fixationTargetDimensions.height / 2
      ) {
        return false;
      }

      return true;
    },
    [blindSpotVanishingXDistancePx, screenHeightPx, screenWidthPx, testData.visualFieldSettings],
  );

  const isEyeBlindSpot = useCallback(
    (currentDot) => {
      if (currentDot['blindspot']) {
        if (testData.eyeTested !== 'bothEyes') {
          if (testData.eyeTested === 'leftEye' && (currentDot['orientation'] === 'topLeft' || currentDot['orientation'] === 'bottomLeft')) {
            return true;
          } else if (testData.eyeTested === 'rightEye' && (currentDot['orientation'] === 'topRight' || currentDot['orientation'] === 'bottomRight')) {
            return true;
          }
        }
      }

      return false;
    },
    [testData.eyeTested],
  );

  const isOppositeEyeBlindSpot = useCallback(
    (currentDot) => {
      if (currentDot['blindspot']) {
        if (testData.eyeTested !== 'bothEyes') {
          if (testData.eyeTested === 'leftEye' && (currentDot['orientation'] === 'topRight' || currentDot['orientation'] === 'bottomRight')) {
            return true;
          } else if (testData.eyeTested === 'rightEye' && (currentDot['orientation'] === 'topLeft' || currentDot['orientation'] === 'bottomLeft')) {
            return true;
          }
        } else {
          return true;
        }
      }

      return false;
    },
    [testData.eyeTested],
  );

  const getDotData = useCallback(() => {
    let hasBoundariesWarning = false;
    let localTotalDots = 0;
    let localBlindSpots = 0;

    let localDots = [...forthValleyVisualFieldDots];

    localDots.forEach((individualDot) => {
      if (isOutOfBoundaries(individualDot)) {
        hasBoundariesWarning = true;
        individualDot['outOfBoundaries'] = true;
        individualDot['disabled'] = true;
      } else if (isOppositeEyeBlindSpot(individualDot)) {
        individualDot['disabled'] = true;
      } else if (isEyeBlindSpot(individualDot) === false) {
        delete individualDot['outOfBoundaries'];
        delete individualDot['disabled'];
      }
      individualDot['id'] = `coord${individualDot.degreeX}${individualDot.degreeY}`;

      if (!individualDot['disabled'] && !individualDot['blindspot']) {
        localTotalDots += 1;
      }

      if (individualDot['blindspot'] && !individualDot['disabled']) {
        localBlindSpots += 1;
      }
    });

    return { localDots, localTotalDots, localBlindSpots, hasBoundariesWarning };
  }, [isEyeBlindSpot, isOppositeEyeBlindSpot, isOutOfBoundaries]);

  return getDotData;
};

export default useDots;
